import {
  calculateResponsiveFontSize,
  CustomTheme,
} from '../../../themes/themeUtils';
import parse from 'html-react-parser';

export const preserveWhitespace = (html: string) => {
  return html.replace(/ {2,}/g, (match) => match.replace(/ /g, '&nbsp;'));
};

export const replaceFontSizes = (
  breakpointMultiplier: number,
  html: string,
  theme: CustomTheme
): string => {
  return html.replace(
    /font-size\s*:\s*([0-9.]+)(px)?/gi,
    (match, size, unit) => {
      const numericSize = parseFloat(size);
      const converted = calculateResponsiveFontSize(
        breakpointMultiplier,
        numericSize,
        theme
      );
      return `font-size: ${converted}rem`;
    }
  );
};

export const isRichText = (input: string): boolean => {
  return /<[^>]+>/.test(input);
};

// convert and wrap pure string in basic rich text format
export const ensureRichTextFormat = (input: string) => {
  if (!input?.trim()) return '';
  const visibleText = input
    .replace(/<br\s*\/?>/gi, '')
    .replace(/&nbsp;/gi, '')
    .replace(/<\/?[^>]+(>|$)/g, '')
    .trim();
  if (!visibleText) return undefined;
  return isRichText(input) ? input : `<p>${input}</p>`;
};

export const moveSpanStylesToParent = (html: string): string => {
  return html.replace(
    /<(\w+)([^>]*)>(.*?)<\/\1>/gis,
    (_match, parentTag, parentAttrs, innerHtml) => {
      const spanMatch = innerHtml.match(
        /^(\s*)<span([^>]*)style="([^"]+)"([^>]*)>(.*?)<\/span>(.*)$/is
      );
      if (!spanMatch)
        return `<${parentTag}${parentAttrs}>${innerHtml}</${parentTag}>`;

      const [
        _full,
        leadingWhitespace,
        spanAttrsBefore,
        spanStyle,
        spanAttrsAfter,
        spanContent,
        trailingContent,
      ] = spanMatch;

      const existingStyleMatch = parentAttrs.match(/style="([^"]*)"/i);
      const existingStyle = existingStyleMatch ? existingStyleMatch[1] : '';

      const mergedStyle = `${existingStyle}${spanStyle}`;

      const cleanedParentAttrs = parentAttrs
        .replace(/style="[^"]*"/i, '')
        .trim();
      const space = cleanedParentAttrs ? ' ' : '';

      return `<${parentTag}${space}${cleanedParentAttrs} style="${mergedStyle}">${leadingWhitespace}${spanContent}${trailingContent}</${parentTag}>`;
    }
  );
};

export const getTransformForTextOverlayContent = (
  html: string,
  top: number,
  left: number
) => {
  const parsedHtml = parse(html) as any;
  const arr = Array.isArray(parsedHtml) ? parsedHtml : [parsedHtml];

  // default margins by tag
  const defaultMargins: Record<string, number> = {
    h1: 22,
    h2: 22,
    h3: 22,
    h4: 22,
    h5: 22,
    h6: 22,
    p: 16,
  };

  // adjust for default margins for first/last node so the text aligns exactly at the image border
  const targetNode =
    top === 0 ? arr[0] : top === 100 ? arr[arr.length - 1] : null;

  const marginAdjust = targetNode?.type
    ? defaultMargins[targetNode.type.toString().toLowerCase()] ?? 0
    : 0;

  // translate X and Y depending on the left and the top alignment respectively
  const tx = left === 0 ? '0' : left === 100 ? '-100%' : '-50%';
  const ty = top === 0 ? '0%' : top === 100 ? '-100%' : '-50%';

  return top === 0
    ? `translate(${tx}, calc(${ty} - ${marginAdjust}px))`
    : top === 100
    ? `translate(${tx}, calc(${ty} + ${marginAdjust}px))`
    : `translate(${tx}, ${ty})`;
};

export function decorateMatchingBuzzwordReferences(
  html: string,
  htmlId: string,
  cssClassNames: string,
  options?: { matcher?: string }
): string {
  if (!html) return html;

  // match either: <span ...>response:key</span>  or  response:key
  const pattern =
    /<span\b([^>]*)>(response:[^\s<]+)<\/span>|(response:[^\s<]+)/gi;

  const mergeClasses = (classList: string) =>
    Array.from(new Set(classList.split(/\s+/).filter(Boolean))).join(' ');

  return html.replace(
    pattern,
    (
      _full,
      spanAttributes: string,
      spanContent: string,
      plainContent: string
    ) => {
      const token = spanContent || plainContent;

      if (options?.matcher) {
        const matches = token.toLowerCase() === options.matcher.toLowerCase();
        if (!matches) return _full || token; // leave unchanged
      }

      if (spanContent) {
        // already inside span
        const classAttrMatch = spanAttributes?.match(/\bclass="([^"]*)"/i);
        const existingClasses = classAttrMatch ? classAttrMatch[1] : '';
        const combinedClasses = mergeClasses(
          [existingClasses, cssClassNames].join(' ')
        );
        const preservedAttrs = (spanAttributes || '')
          .replace(/\s*\bclass="[^"]*"/gi, '')
          .replace(/\s*\bid="[^"]*"/gi, '')
          .trim();

        const attrParts = [
          preservedAttrs,
          `class="${combinedClasses}"`,
          `id="${htmlId}"`,
        ]
          .filter(Boolean)
          .join(' ');
        return `<span ${attrParts}>${spanContent}</span>`;
      }

      // not wrapped in a span
      return `<span class="${cssClassNames}" id="${htmlId}">${plainContent}</span>`;
    }
  );
}
